import { lineHasCatalogHandset, TIPO_VENDA_VF_24X, WTTX_APARELHO_VALUE } from './handsetLine';

export type CqvRoteiroLine = {
  negociacao?: string;
  contractType?: string;
  planName?: string;
  plano?: string;
  quantidade?: number;
  valorMensal?: number;
  valorTotalMensal?: number;
  excluirDoContrato?: boolean;
  requerSuspensao?: boolean;
  aparelho?: string | null;
  handsetDeviceId?: string | null;
  aparelhoValorMensal?: number | null;
  aparelhoValorTotal?: number | null;
  tipoVenda?: string | null;
  ddd?: string;
  numero?: string;
  ehPortabilidade?: boolean;
  portabilidadeNomeCompleto?: string;
  portabilidadeCpf?: string;
  portabilidadeOperadora?: string;
  portabilidadeLinha?: string;
};

export type CqvRoteiroContext = {
  administrador: string;
  cnpj: string;
  cpfAdministrador: string;
  telefoneContato: string;
  currentUserName: string;
  contractCreatorName: string;
  teamName: string;
  managerName: string;
  totalRenovacoes: number;
  totalPortabilidades: number;
  totalLinhasNovas: number;
  totalAparelhos: number;
  detalhesPlanos: string;
  valorPlanos: number;
  valorRenegociacao: number;
  valorPortabilidade: number;
  valorLinhasNovas: number;
  hasRenegAndAditivo: boolean;
  hasMultipleCategorias: boolean;
  linhasAparelhoTexto: string;
  bloqueioSuspensas: string;
  /** Legado — não usado no script de ligação (endereço vai para `enderecoEntregaBloco`). */
  deliveryBlock: string;
  /** Bloco "Endereço de Entrega" no final do script de ligação. */
  enderecoEntregaBloco: string;
  prazoEntrega: string;
  isOnlyReneg: boolean;
  fastchip: boolean;
  hasWttx: boolean;
  wttxDetalhesLinhas?: string;
  withDevices: boolean;
};

export function isActiveCqvLine(line: CqvRoteiroLine): boolean {
  return !line.excluirDoContrato && !line.requerSuspensao;
}

function formatCurrencyCqv(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

function sumPlanValorLines(lines: CqvRoteiroLine[]): number {
  return lines.reduce((sum, linha) => sum + planValorLinha(linha), 0);
}

function isRenegLine(line: CqvRoteiroLine): boolean {
  return line.negociacao === 'Renegociação' || line.contractType === 'Renegociação';
}

function isAditivoLine(line: CqvRoteiroLine): boolean {
  return line.negociacao === 'Aditivo' || line.contractType === 'Aditivo';
}

export function isPortabilidadeLine(line: CqvRoteiroLine): boolean {
  return isAditivoLine(line) && !!line.ehPortabilidade;
}

export function isLinhaNovaSemPortabilidade(line: CqvRoteiroLine): boolean {
  return isAditivoLine(line) && !line.ehPortabilidade;
}

function formatPortabilidadeDetalhe(linha: CqvRoteiroLine): string {
  const op = (linha.portabilidadeOperadora ?? '').trim();
  const num = (linha.portabilidadeLinha ?? '').trim();
  const parts: string[] = [];
  if (op) parts.push(`OP: ${op.toUpperCase()}`);
  if (num) parts.push(`LINHA: ${num}`);
  return parts.length ? ` (${parts.join(' | ')})` : '';
}

/** Monta segmentos de abertura omitindo categorias com total 0. */
export function formatCategoriasAbertura(
  totalRenovacoes: number,
  totalPortabilidades: number,
  totalLinhasNovas: number
): string {
  const parts: string[] = [];
  if (totalRenovacoes > 0) {
    parts.push(
      `${totalRenovacoes} ${totalRenovacoes === 1 ? 'RENOVAÇÃO' : 'RENOVAÇÕES'}`
    );
  }
  if (totalPortabilidades > 0) {
    parts.push(
      `${totalPortabilidades} ${totalPortabilidades === 1 ? 'PORTABILIDADE' : 'PORTABILIDADES'}`
    );
  }
  if (totalLinhasNovas > 0) {
    parts.push(
      `${totalLinhasNovas} ${totalLinhasNovas === 1 ? 'LINHA NOVA' : 'LINHAS NOVAS'}`
    );
  }
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} E ${parts[1]}`;
  return `${parts[0]}, ${parts[1]} E ${parts[2]}`;
}

export function generatePlanosText(
  lines: CqvRoteiroLine[],
  options?: { incluirValoresSeparados?: boolean }
): string {
  if (!lines?.length) return '';

  const renovacoes = lines.filter(isRenegLine);
  const portabilidades = lines.filter(isPortabilidadeLine);
  const linhasNovas = lines.filter(isLinhaNovaSemPortabilidade);
  const incluirValoresSeparados = options?.incluirValoresSeparados ?? false;

  let texto = '';

  if (renovacoes.length > 0) {
    const planosRenovacao: Record<string, number> = {};
    renovacoes.forEach((linha) => {
      const planName = linha.planName || linha.plano || 'Plano não identificado';
      planosRenovacao[planName] = (planosRenovacao[planName] || 0) + (linha.quantidade || 1);
    });
    Object.entries(planosRenovacao).forEach(([planName, quantidade]) => {
      texto += `${quantidade} ${quantidade === 1 ? 'RENOVAÇÃO' : 'RENOVAÇÕES'} NO PLANO ${planName.toUpperCase()}\n`;
    });
    if (incluirValoresSeparados && renovacoes.length > 0) {
      texto += `VALOR DAS RENOVAÇÕES: R$ ${formatCurrencyCqv(sumPlanValorLines(renovacoes))}\n`;
    }
  }

  if (portabilidades.length > 0) {
    portabilidades.forEach((linha) => {
      const planName = linha.planName || linha.plano || 'Plano não identificado';
      const quantidade = linha.quantidade || 1;
      const detalhe = formatPortabilidadeDetalhe(linha);
      texto += `${quantidade} ${quantidade === 1 ? 'PORTABILIDADE' : 'PORTABILIDADES'} NO PLANO ${planName.toUpperCase()}${detalhe}\n`;
    });
    if (incluirValoresSeparados && portabilidades.length > 0) {
      texto += `VALOR DAS PORTABILIDADES: R$ ${formatCurrencyCqv(sumPlanValorLines(portabilidades))}\n`;
    }
  }

  if (linhasNovas.length > 0) {
    const planosNovos: Record<string, number> = {};
    linhasNovas.forEach((linha) => {
      const planName = linha.planName || linha.plano || 'Plano não identificado';
      planosNovos[planName] = (planosNovos[planName] || 0) + (linha.quantidade || 1);
    });
    Object.entries(planosNovos).forEach(([planName, quantidade]) => {
      texto += `${quantidade} ${quantidade === 1 ? 'LINHA NOVA' : 'LINHAS NOVAS'} NO PLANO ${planName.toUpperCase()}\n`;
    });
    if (incluirValoresSeparados && linhasNovas.length > 0) {
      texto += `VALOR DAS LINHAS NOVAS: R$ ${formatCurrencyCqv(sumPlanValorLines(linhasNovas))}\n`;
    }
  }

  return texto.trim();
}

function planValorLinha(linha: CqvRoteiroLine): number {
  return (linha.valorMensal ?? linha.valorTotalMensal ?? 0) * (linha.quantidade || 1);
}

function formatWhatsappValoresPlanos(ctx: CqvRoteiroContext): string {
  if (ctx.hasMultipleCategorias) {
    const lines: string[] = [];
    if (ctx.totalRenovacoes > 0) {
      lines.push(`💰 Renovações: R$ ${formatCurrencyCqv(ctx.valorRenegociacao)}`);
    }
    if (ctx.totalPortabilidades > 0) {
      lines.push(`💰 Portabilidades: R$ ${formatCurrencyCqv(ctx.valorPortabilidade)}`);
    }
    if (ctx.totalLinhasNovas > 0) {
      lines.push(`💰 Linhas novas: R$ ${formatCurrencyCqv(ctx.valorLinhasNovas)}`);
    }
    lines.push(`💰 Valor total dos planos: R$ ${formatCurrencyCqv(ctx.valorPlanos)}`);
    return lines.join('\n');
  }
  return `💰 VALOR DOS PLANOS: R$ ${formatCurrencyCqv(ctx.valorPlanos)}`;
}

/** Agrupa aparelhos por nome + parcela para o script. */
export function buildAparelhosScriptLines(lines: CqvRoteiroLine[]): {
  totalAparelhos: number;
  textoLinhas: string;
} {
  const comAparelho = lines.filter((l) => lineHasCatalogHandset(l));
  const totalAparelhos = comAparelho.reduce((s, l) => s + (l.quantidade || 1), 0);
  const grupos = new Map<string, { qty: number; parcela: number }>();

  comAparelho.forEach((l) => {
    const nome = (l.aparelho ?? 'APARELHO').trim().toUpperCase();
    const parcela = l.aparelhoValorMensal ?? 0;
    const key = `${nome}|${parcela}`;
    const prev = grupos.get(key) ?? { qty: 0, parcela };
    grupos.set(key, { qty: prev.qty + (l.quantidade || 1), parcela });
  });

  const textoLinhas = Array.from(grupos.entries())
    .map(([key, g]) => {
      const nome = key.split('|')[0];
      const parcelaStr = g.parcela.toFixed(2).replace('.', ',');
      const valorParte = `EM VENDA FACILITADA — R$ ${parcelaStr}`;
      if (g.qty === 1) {
        return `+ 1 ${nome} ${valorParte} (VALOR UNITÁRIO)`;
      }
      return `+ ${g.qty} ${nome} ${valorParte} POR APARELHO`;
    })
    .join('\n');

  return { totalAparelhos, textoLinhas };
}

export function buildSuspensasBlock(linhasSuspensas: CqvRoteiroLine[]): string {
  if (!linhasSuspensas.length) return '';
  const cabecalho =
    linhasSuspensas.length === 1
      ? '⚠️ IMPORTANTE: 1 LINHA MARCADA PARA DESATIVAÇÃO — em contratos sujeitos à nova política, oriente o cancelamento pelo 1056 (TIM):'
      : `⚠️ IMPORTANTE: ${linhasSuspensas.length} LINHAS MARCADAS PARA DESATIVAÇÃO — em contratos sujeitos à nova política, oriente o cancelamento pelo 1056 (TIM):`;
  return `

${cabecalho}${linhasSuspensas
    .map(
      (l) =>
        `\n   - ${l.ddd ?? ''}${l.numero ?? ''} (${l.planName || l.plano || 'Plano TIM'})`
    )
    .join('')}
ESTAS LINHAS NÃO ESTÃO NO VALOR TOTAL ACIMA. Contratos anteriores à data de corte podem continuar com suspensão normalmente; nos contratos novos, o cliente deve cancelar ligando para a TIM no 1056.`;
}

export type BuildCqvContextInput = {
  linhasAtivas: CqvRoteiroLine[];
  linhasSuspensas: CqvRoteiroLine[];
  administrador: string;
  cnpj: string;
  cpfAdministrador: string;
  telefoneAdministrador?: string;
  telefone2Administrador?: string;
  currentUserName: string;
  contractCreatorName: string;
  teamName: string;
  managerName: string;
  deliveryBlock: string;
  deliveryAddress?: {
    cep?: string;
    endereco?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    referencia?: string;
  } | null;
  isOnlyReneg: boolean;
  fastchip: boolean;
  hasWttx: boolean;
  wttxDetalhesLinhas?: string;
};

export type CqvDeliveryAddress = NonNullable<BuildCqvContextInput['deliveryAddress']>;

/** Saudação do script de ligação conforme horário local. */
export function getCqvLigacaoSaudacao(date: Date = new Date()): string {
  const h = date.getHours();
  if (h < 12) return 'BOM DIA';
  if (h < 18) return 'BOA TARDE';
  return 'BOA NOITE';
}

/** Formata o bloco de endereço de entrega no final do script de ligação (padrão CQV). */
export function formatCallScriptEnderecoEntrega(
  deliveryAddress?: CqvDeliveryAddress | null
): string {
  const da = deliveryAddress;
  const hasStreet = da && String(da.endereco || '').trim().length > 0;
  const cepDigits = da?.cep ? String(da.cep).replace(/\D/g, '') : '';
  const hasCep = cepDigits.length === 8;

  if (!da || (!hasStreet && !hasCep)) {
    return `Endereço de Entrega
Endereço: não consta completo no cadastro — confirmar na ligação o local combinado no contrato.`;
  }

  const cepFmt = hasCep ? `${cepDigits.slice(0, 5)}-${cepDigits.slice(5)}` : da.cep || '—';
  const logr = [da.endereco, da.numero].filter(Boolean).join(', ');
  const comp = da.complemento ? ` - ${da.complemento}` : '';
  const cidadeUf = `${da.cidade || '—'}/${da.estado || '—'}`;

  return `Endereço de Entrega
CEP: ${cepFmt}
Endereço: ${logr || '—'}${comp}
Bairro: ${da.bairro || '—'}
Cidade/UF: ${cidadeUf}`;
}

export function buildCqvScriptContext(input: BuildCqvContextInput): CqvRoteiroContext {
  const renovacoes = input.linhasAtivas.filter(isRenegLine);
  const portabilidades = input.linhasAtivas.filter(isPortabilidadeLine);
  const linhasNovas = input.linhasAtivas.filter(isLinhaNovaSemPortabilidade);
  const totalRenovacoes = renovacoes.reduce((s, l) => s + (l.quantidade || 1), 0);
  const totalPortabilidades = portabilidades.reduce((s, l) => s + (l.quantidade || 1), 0);
  const totalLinhasNovas = linhasNovas.reduce((s, l) => s + (l.quantidade || 1), 0);
  const totalAditivo = totalPortabilidades + totalLinhasNovas;
  const hasRenegAndAditivo = totalRenovacoes > 0 && totalAditivo > 0;
  const categoriasAtivas = [totalRenovacoes, totalPortabilidades, totalLinhasNovas].filter(
    (n) => n > 0
  ).length;
  const hasMultipleCategorias = categoriasAtivas > 1;
  const { totalAparelhos, textoLinhas } = buildAparelhosScriptLines(input.linhasAtivas);
  const withDevices = totalAparelhos > 0;

  const valorRenegociacao = sumPlanValorLines(renovacoes);
  const valorPortabilidade = sumPlanValorLines(portabilidades);
  const valorLinhasNovas = sumPlanValorLines(linhasNovas);
  const valorPlanos = valorRenegociacao + valorPortabilidade + valorLinhasNovas;

  const tel =
    input.telefoneAdministrador?.trim() ||
    input.telefone2Administrador?.trim() ||
    '';

  const prazoBase = input.isOnlyReneg
    ? ''
    : input.fastchip
      ? withDevices
        ? 'O PRAZO DE ENTREGA DO CHIP E DO APARELHO É DE 7 A 15 DIAS ÚTEIS NO ENDEREÇO DE ENTREGA (FASTCHIP — CONFIRMAR COM O CLIENTE).'
        : 'O PRAZO DE ENTREGA DO CHIP É DE 7 A 15 DIAS ÚTEIS NO ENDEREÇO DE ENTREGA (FASTCHIP — CONFIRMAR COM O CLIENTE).'
      : withDevices
        ? 'O PRAZO DE ENTREGA DO CHIP E DO APARELHO É DE 7 A 15 DIAS ÚTEIS NO ENDEREÇO DE ENTREGA.'
        : 'O PRAZO DE ENTREGA DO CHIP É DE 7 A 15 DIAS ÚTEIS NO ENDEREÇO DE ENTREGA.';

  const enderecoEntregaBloco = input.isOnlyReneg
    ? ''
    : formatCallScriptEnderecoEntrega(input.deliveryAddress);

  return {
    administrador: input.administrador,
    cnpj: input.cnpj,
    cpfAdministrador: input.cpfAdministrador,
    telefoneContato: tel,
    currentUserName: input.currentUserName,
    contractCreatorName: input.contractCreatorName,
    teamName: input.teamName,
    managerName: input.managerName,
    totalRenovacoes,
    totalPortabilidades,
    totalLinhasNovas,
    totalAparelhos,
    detalhesPlanos: generatePlanosText(input.linhasAtivas, {
      incluirValoresSeparados: true,
    }),
    valorPlanos,
    valorRenegociacao,
    valorPortabilidade,
    valorLinhasNovas,
    hasRenegAndAditivo,
    hasMultipleCategorias,
    linhasAparelhoTexto: textoLinhas,
    bloqueioSuspensas: buildSuspensasBlock(input.linhasSuspensas),
    deliveryBlock: input.deliveryBlock,
    enderecoEntregaBloco: enderecoEntregaBloco,
    prazoEntrega: prazoBase,
    isOnlyReneg: input.isOnlyReneg,
    fastchip: input.fastchip,
    hasWttx: input.hasWttx,
    wttxDetalhesLinhas: input.wttxDetalhesLinhas,
    withDevices,
  };
}

function formatMotivoLinha(ctx: CqvRoteiroContext): string {
  const categorias = formatCategoriasAbertura(
    ctx.totalRenovacoes,
    ctx.totalPortabilidades,
    ctx.totalLinhasNovas
  );
  if (!categorias) {
    return 'ESTOU ENTRANDO EM CONTATO PRA CONFIRMAR O PEDIDO NO CNPJ DA EMPRESA.';
  }
  return `ESTOU ENTRANDO EM CONTATO PRA CONFIRMAR ${categorias} NO CNPJ DA EMPRESA.`;
}

function formatCallScriptContratacaoBloco(ctx: CqvRoteiroContext): string {
  const lines: string[] = [];
  if (ctx.detalhesPlanos.trim()) {
    lines.push(ctx.detalhesPlanos.trim());
  }
  if (ctx.linhasAparelhoTexto.trim()) {
    lines.push(ctx.linhasAparelhoTexto.trim());
  }
  lines.push(`COM O VALOR TOTAL DE R$ ${formatCurrencyCqv(ctx.valorPlanos)}`);
  return lines.join('\n');
}

function formatCallScriptEquipeBloco(ctx: CqvRoteiroContext): string {
  const gerente = (ctx.managerName || 'GERENTE NÃO IDENTIFICADO').toUpperCase();
  return `CONTRATO CRIADO POR: ${ctx.contractCreatorName.toUpperCase()}
TIME: ${ctx.teamName.toUpperCase()}
GERENTE RESPONSÁVEL: ${gerente}`;
}

function formatCallScriptCorpoComum(
  ctx: CqvRoteiroContext,
  saudacao: string,
  motivoLinha: string,
  contratacaoDetalhe: string
): string {
  const telLinha = ctx.telefoneContato
    ? `ESTAMOS EM CONTATO NO NUMERO DE TELEFONE: ${ctx.telefoneContato}`
    : '';
  const prazoBloco = ctx.prazoEntrega ? `\n${ctx.prazoEntrega}` : '';
  const enderecoBloco = ctx.enderecoEntregaBloco
    ? `\n\n${ctx.enderecoEntregaBloco}`
    : '';

  return `ALO ${saudacao} GOSTARIA DE FALAR COM ${ctx.administrador.toUpperCase()}!
MEU NOME É ${ctx.currentUserName.toUpperCase()}, FALO DA GETTEL TELECOM, REPRESENTANTE DA TIM.

${motivoLinha}
PODEMOS CONFIRMAR OS DADOS DO SEU PEDIDO?!

SÓ INFORMANDO QUE A LIGAÇÃO FICA REGISTRADA.
${telLinha ? `${telLinha}\n` : ''}
O CNPJ DA EMPRESA É ${ctx.cnpj}
PODE ME CONFIRMAR O SEU NOME COMPLETO E CPF POR GENTILEZA?

(CLIENTE IRÁ CONFIRMAR: ${ctx.administrador.toUpperCase()} – ${ctx.cpfAdministrador})

${formatCallScriptEquipeBloco(ctx)}

A CONTRATAÇÃO FICOU:
${contratacaoDetalhe}${ctx.bloqueioSuspensas}

CORRETO? (Confirmar com o cliente se o valor esta correto)

VOCÊ REALIZOU A ASSINATURA DO CONTRATO POR ACEITE DIGITAL NO WHATSAPP OU E-MAIL?

LEMBRANDO QUE O PRAZO DE FIDELIDADE É DE 24 MESES.
O PEDIDO VAI PARA ANÁLISE DE CRÉDITO E DOCUMENTAÇÃO.${prazoBloco}${enderecoBloco}

REFERENTE A CONTRATAÇÃO TEM ALGUMA DÚVIDA?!
OBRIGADA E UM ÓTIMO DIA!`;
}

export function formatCallScript(
  ctx: CqvRoteiroContext,
  options?: { saudacao?: string; now?: Date }
): string {
  const saudacao = options?.saudacao ?? getCqvLigacaoSaudacao(options?.now);

  if (ctx.hasWttx && ctx.wttxDetalhesLinhas) {
    const categorias = formatCategoriasAbertura(
      ctx.totalRenovacoes,
      ctx.totalPortabilidades,
      ctx.totalLinhasNovas
    );
    const motivo = categorias
      ? `ESTOU ENTRANDO EM CONTATO PRA CONFIRMAR ${categorias} TIM EMPRESAS INTERNET NO CNPJ DA EMPRESA.`
      : 'ESTOU ENTRANDO EM CONTATO PRA CONFIRMAR TIM EMPRESAS INTERNET NO CNPJ DA EMPRESA.';
    const contratacao = `${ctx.wttxDetalhesLinhas}
COM O VALOR TOTAL DE R$ ${formatCurrencyCqv(ctx.valorPlanos)}`;
    return formatCallScriptCorpoComum(ctx, saudacao, motivo, contratacao);
  }

  const motivoLinha = formatMotivoLinha(ctx);
  const contratacaoBloco = formatCallScriptContratacaoBloco(ctx);
  return formatCallScriptCorpoComum(ctx, saudacao, motivoLinha, contratacaoBloco);
}

export type WhatsappPart1Input = {
  ctx: CqvRoteiroContext;
  primeiroNome: string;
  razao: string;
  cnpjLinha: string;
  operador: string;
  saudacao: string;
  enderecoBloco: string;
};

export function formatWhatsappCqvPart1(input: WhatsappPart1Input): string {
  const { ctx, primeiroNome, razao, cnpjLinha, operador, saudacao, enderecoBloco } = input;
  const blocoPlanos = ctx.detalhesPlanos.trim() || '(detalhes dos planos conforme contrato)';
  const valoresBloco = formatWhatsappValoresPlanos(ctx);
  const aparelhosBloco = ctx.linhasAparelhoTexto
    ? `\n${ctx.linhasAparelhoTexto.replace(/\+ /g, '📱 ')}`
    : '';
  const contagemPartes: string[] = [];
  if (ctx.totalRenovacoes > 0) contagemPartes.push(`${ctx.totalRenovacoes} renovação(ões)`);
  if (ctx.totalPortabilidades > 0) contagemPartes.push(`${ctx.totalPortabilidades} portabilidade(s)`);
  if (ctx.totalLinhasNovas > 0) contagemPartes.push(`${ctx.totalLinhasNovas} linha(s) nova(s)`);
  if (ctx.withDevices && ctx.totalAparelhos > 0) {
    contagemPartes.push(`${ctx.totalAparelhos} aparelho(s)`);
  }
  const contagemExtra =
    contagemPartes.length > 0 ? `\n📦 ${contagemPartes.join(', ')}.` : '';

  const prazoChip = ctx.withDevices
    ? '🚚 O prazo de entrega do chip e do aparelho é de 7 a 15 dias úteis!'
    : '🚚 O prazo de entrega do chip é de 7 a 15 dias úteis!';

  return (
    `${saudacao} ${primeiroNome}! 👋\n\n` +
    `Meu nome é ${operador}, falo da Gettel, representante da Tim. 📞\n\n` +
    `Estou entrando em contato pra confirmar o pedido que foi solicitado para o Cnpj da empresa. ✅${contagemExtra}\n\n` +
    `DADOS DA EMPRESA 🏢\n\n` +
    `Razão Social: ${razao}\n` +
    `Cnpj da empresa: ${cnpjLinha}\n\n` +
    `A CONTRATAÇÃO FICOU 📱\n` +
    `${blocoPlanos}\n` +
    `${valoresBloco}${aparelhosBloco}\n\n` +
    `⏳ Lembrando que o prazo de fidelidade é de 24 meses.\n` +
    `📑 O pedido vai para análise de crédito e documentação.\n` +
    `${prazoChip}\n` +
    `${enderecoBloco}\n\n` +
    `🔐 Por questões de segurança e para poder seguir com seu pedido, vou precisar das seguintes informações:\n` +
    `📝 NOME COMPLETO:\n` +
    `🪪 CPF:`
  );
}
